/**
 * Personal Details card: the rows shown from a rep's Current Database row
 * (`GET /api/mySeason/database`, docType `resort-db`, "P24 Resort Team
 * Database" / Current Database tab). Keys are the sheet's own headers; labels
 * come from the brand dictionary (`personalDetails.*`) so a brand can relabel
 * a field (Wasteland shows `GBS ID` as "SnowFuze No").
 *
 * Passport details are not here any more: they live on the Staff Flights tab
 * per booking and will come with the flights data.
 */

const EMPTY = '—'

/**
 * Sync stores every cell as the string Google Sheets displays (the values API
 * default, FORMATTED_VALUE), so a date cell arrives as text in the sheet's own
 * format. ISO `YYYY-MM-DD` (with or without a time part) is shown as
 * `DD/MM/YYYY`; anything else is already display text and is left alone.
 */
export function formatSheetDate(value) {
  if (value == null) return null
  const text = String(value).trim()
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/)
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`
  return text
}

function present(value) {
  return value != null && String(value).trim() !== ''
}

function text(value) {
  return present(value) ? String(value).trim() : EMPTY
}

/**
 * @param {object|null|undefined} data one Current Database row
 * @param {object} dict compiled brand dictionary (`__DICT__`); only `personalDetails.*` is read
 * @returns {Array<{key: string, label: string, lines: string[]}>}
 */
export function personalDetailRows(data, dict) {
  const d = data || {}
  const t = (dict && dict.personalDetails) || {}
  const label = (key, fallback) => t[key] || fallback

  const simple = (key, labelKey, fallback, format = text) => ({
    key,
    label: label(labelKey, fallback),
    lines: [format(d[key])]
  })

  const kinName = [d['Next of Kin First Name'], d['Next of Kin Last Name']]
    .filter(present).map(v => String(v).trim()).join(' ')
  const kinLines = [
    present(d['Next of Kin Relationship']) && kinName
      ? `${kinName} (${String(d['Next of Kin Relationship']).trim()})`
      : (kinName || (present(d['Next of Kin Relationship']) ? String(d['Next of Kin Relationship']).trim() : null)),
    present(d['Next of Kin Phone Number']) ? String(d['Next of Kin Phone Number']).trim() : null
  ].filter(Boolean)

  return [
    simple('GBS ID', 'gbsId', 'GBS ID'),
    simple('Preferred First Name', 'preferredFirstName', 'Preferred first name'),
    simple('First Name', 'firstName', 'First name'),
    simple('Last Name', 'lastName', 'Last name'),
    simple('Pronouns', 'pronouns', 'Pronouns'),
    simple('Date of Birth', 'dateOfBirth', 'Date of birth', v => text(formatSheetDate(v))),
    simple('Nationality', 'nationality', 'Nationality'),
    simple('Email', 'email', 'Email'),
    simple('Phone Number', 'phoneNumber', 'Phone number'),
    simple('Preferred Airport', 'preferredAirport', 'Preferred airport'),
    simple('Jacket Address Full', 'jacketAddress', 'Jacket address'),
    simple('Preferred Jacket Size', 'jacketSize', 'Jacket size'),
    simple('Jacket Number', 'jacketNumber', 'Jacket number'),
    simple('Equipment Hire', 'equipmentHire', 'Equipment hire'),
    simple('Helmet Hire', 'helmetHire', 'Helmet hire'),
    simple('Lift Pass Requirements', 'liftPassRequirements', 'Lift pass requirements'),
    simple('Luggage Requirements', 'luggageRequirements', 'Luggage requirements'),
    simple('French', 'french', 'French'),
    simple('Authorised Driver (Y/N)', 'authorisedDriver', 'Authorised driver'),
    simple('Approved Vehicles', 'approvedVehicles', 'Approved vehicles'),
    {
      key: 'Next of Kin',
      label: label('nextOfKin', 'Next of kin'),
      lines: kinLines.length ? kinLines : [EMPTY]
    }
  ]
}
